import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import translate from "@insite/client-framework/Translate";
import { StyledButton } from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps, GridOffset, GridWrapper } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps, GridItemStyle, GridWidths } from "@insite/mobius/GridItem";
import { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { TypographyProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import PromotionalContentCardDesign from "@NA/Components/PromotionalContentCardDesign";
import toggleLoginDrawer from "@NA/Handlers/ADILoginDrawerHandler";
import toggleRewardsDrawer from "@NA/Handlers/ADIRewardsDrawerHandler";
import { MappedLink } from "@NA/Overrides/Widgets/Header/MainNavigation";
import CustomApplicationState from "@NA/Store/Data/CustomApplicationState/CustomApplicationState";
import React, { useEffect, useRef, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";
import { handleZoneLinkClick } from "@NA/Utils/Utility";

export interface BrandSectionStyles {
    brandsContainer: GridContainerProps;
    brandsFirstGridItem: GridItemProps;
    brandsSecondGridItem: GridItemProps;
    brandsImageGridItem: GridItemProps;
    leftMegaMenuItem?: GridItemProps;
    leftMegaMenuLink: LinkPresentationProps;
    PromoContainer?: InjectableCss;
}

export const brandSectionStyles: BrandSectionStyles = {
    brandsContainer: {
        css: css`
            height: auto;
            > ${GridOffset} {
                height: auto;
                width: 100%;
                margin: auto;
                flex-wrap: nowrap;
                ${({ theme }: { theme: BaseTheme; }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    null,
                    null,
                    css`
                            padding: 0px 9%;
                        `,
                ])}
            }
        `,
    },
    brandsFirstGridItem: {
        css: css`
            padding: 0px;
            max-width: 17%;
            flex-basis: 17%;
            flex-direction: column;
            overflow: hidden auto;
            > li {
                max-width: 100%;
                padding: 0px 15px 10px;
                margin-left: 5px;
                font-size: 12px;
                height: 28px;
                max-height: 28px;
            }
        `,
    },
    brandsSecondGridItem: {
        css: css`
            max-width: 83%;
            flex-basis: 83%;
            padding: 0px 11px;
            overflow: hidden auto;
        `,
    },
    brandsImageGridItem: {
        css: css`
            flex-basis: 100%;
            max-width: 100%;
            padding: 0px;
            flex-direction: row;
            gap: 10px;

            > div > div:first-child {
                flex-basis: 76%;
                max-width: 76%;
                ${GridWrapper} {
                    width: 100%;
                    ${GridOffset} {
                        display: flex;
                        flex-direction: row;
                        flex-wrap: wrap;
                        margin: 0;
                        width: calc(100% + 10px);
                        margin-left: -5px;

                        @media screen and (max-width: 768px) {
                            width: 100%;
                        }

                        > ${GridItemStyle} {
                            padding: 0;
                            margin: 0 5px 10px 5px;
                            border: solid 1px ${getColor("common.border")};
                            background-color: ${getColor("common.background")};
                            width: calc(100% / 5 - 10px);
                            float: none;
                            min-height: 70px;
                            max-height: 70px;
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            cursor: pointer;
                            border-radius: 3px;
                            flex-basis: auto;
                            max-width: 100%;
                            > a {
                                padding: 10px !important;
                                width: 100%;
                                height: 100%;
                                display: flex !important;
                                justify-content: center;
                                align-items: center;
                                position: relative;
                                text-transform: initial;
                                img {
                                    width: 100%;
                                    height: 100%;
                                    object-fit: contain;
                                }
                            }

                            &:hover {
                                border: 1px solid ${getColor("text.main")};
                            }
                        }
                    }
                }
            }
            > div > div:last-child {
                flex-basis: 24%;
                max-width: 24%;
                a {
                    font-size: 14px;
                    line-height: normal;
                    letter-spacing: normal;
                    color: ${getColor("secondary")};
                    text-decoration: none;
                    text-transform: uppercase;
                    font-weight: 600;
                    img {
                        max-width: 225px;
                        height: 150px;
                    }
                }
            }
        `,
    },
    leftMegaMenuLink: {
        css: css`
            > span {
                color: ${getColor("text.main")} !important;
                font-weight: bold !important;
            }
            &:hover {
                > span {
                    text-decoration: underline;
                }
            }
        `,
    },
    PromoContainer: {
        css: css`
            display: contents;
        `,
    },
};

interface OwnProps {
    link: MappedLink;
    onHandleClose: Function;
    zoneId: string;
}

const mapStateToProps = (state: CustomApplicationState) => {
    return {
        toggleDrawer: state.ADIGlobalData.loginDrawerStore.loginDrawer,
    };
};

const mapDispatchToProps = {
    toggleLoginDrawer,
    toggleRewardsDrawer,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const BrandSectionComponent = ({
    link,
    onHandleClose,
    toggleLoginDrawer,
    toggleRewardsDrawer,
    toggleDrawer,
    zoneId,
}: Props) => {
    const [activeChildIndex, setActiveChildIndex] = useState<number | null>(null);
    const handleMouseEnter = (index: number, categoryName: string) => {
        setActiveChildIndex(index);
    };
    const zoneRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const childLinks = document.querySelectorAll(".child-links");
        const handleLinkClick = () => {
            onHandleClose();
        };
        childLinks.forEach(link => {
            link.addEventListener("click", handleLinkClick);
        });

        return () => {
            childLinks.forEach(link => {
                link.removeEventListener("click", handleLinkClick);
            });
        };
    }, []);

    const handleMouseLeave = () => {
        setActiveChildIndex(null);
    };
    const styles = brandSectionStyles;

    const brandsCategoryArr: MappedLink[] = [];
    const recursiveArray = (item: MappedLink) => {
        if (item.children && item.children.length > 0) {
            item.children[0];
        }
        if (!item.excludeFromNavigation) {
            brandsCategoryArr.push(item);
        }
    };
    link.children &&
        link.children.forEach((item: MappedLink) => {
            recursiveArray(item);
        });

    useEffect(() => {
        if (toggleDrawer) {toggleLoginDrawer({ loginDrawer: false });}
        toggleRewardsDrawer({ rewardsDrawer: false });
    }, []);

    const updateAdiExclusiveUrl = (url: string) => {
        // as per linda request
        if (url && url == "/catalog/adi-exclusive") {
            return "/catalog/featured-brands/adi-exclusive";
        } else {
            return url;
        }
    };

    return (
        <GridContainer {...styles.brandsContainer}>
            <GridItem {...styles.brandsFirstGridItem}>
                {brandsCategoryArr.map((child: MappedLink, childIndex: number) => (
                    <React.Fragment key={`${childIndex}-${child.title}`}>
                        <GridItem
                            className="brand-mega-menu-left-grid-item"
                            {...styles.leftMegaMenuItem}
                            width={(12 / link.numberOfColumns!) as GridWidths}
                            key={`${childIndex}${child.title}`}
                            onMouseOver={() => handleMouseEnter(childIndex, child.title)}
                            onMouseLeave={handleMouseLeave}
                            as="li"
                        >
                            <Link
                                className="brand-mega-menu-left-link"
                                {...styles.leftMegaMenuLink}
                                href={updateAdiExclusiveUrl(child.url)}
                                data-category-name={`selectedCategoryName_${child.title}`}
                                onClick={() => onHandleClose()}
                            >
                                {child.title}
                            </Link>
                        </GridItem>
                    </React.Fragment>
                ))}
            </GridItem>
            <GridItem {...styles.brandsSecondGridItem}>
                <GridItem {...styles.brandsImageGridItem}>
                    <StyledWrapper {...styles.PromoContainer} ref={zoneRef}
                        onClick={(event) => handleZoneLinkClick(event, onHandleClose, zoneRef)}>
                        <Zone zoneName="brands" contentId={zoneId}></Zone>
                        <Zone zoneName="advertising" contentId={zoneId}></Zone>
                    </StyledWrapper>
                </GridItem>
            </GridItem>
        </GridContainer>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(BrandSectionComponent);
